import * as THREE from 'three';
import { VIEWPORT_CAMERA_FOV } from './viewportCameraSync.js';
import {
  BufferGeometry,
  BoxGeometry,
  CylinderGeometry,
  DoubleSide,
  Euler,
  Float32BufferAttribute,
  Group,
  LatheGeometry,
  Line,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  OctahedronGeometry,
  PlaneGeometry,
  Quaternion,
  Raycaster,
  SphereGeometry,
  TorusGeometry,
  Vector2,
  Vector3,
} from 'three';
import store from '../../redux/store';

const _raycaster = new Raycaster();
// Selection outline puts the gizmo on layer 1 — raycast both content + UI layers.
_raycaster.layers.enable(0);
_raycaster.layers.enable(1);

const _tempVector = new Vector3();
const _tempVector2 = new Vector3();
const _tempQuaternion = new Quaternion();
const _tempMatrix = new Matrix4();
const _unit = {
  X: new Vector3(1, 0, 0),
  Y: new Vector3(0, 1, 0),
  Z: new Vector3(0, 0, 1),
};

const _changeEvent = { type: 'change' };
const _mouseDownEvent = { type: 'mouseDown' };
const _mouseUpEvent = { type: 'mouseUp', mode: null };
const _objectChangeEvent = { type: 'objectChange' };

/**
 * Gizmo world scale — perspective uses distance; orthographic also compensates for
 * zoom so on-screen size matches perspective at the same orbit distance.
 */
function computeTransformControlsScaleFactor(
  camera,
  worldPosition,
  cameraPosition,
  size = 0.15,
  multiplier = 1
) {
  const distance = Math.max(worldPosition.distanceTo(cameraPosition), 0.001);
  let scale = (size ?? 0.15) * (distance / 8) * multiplier;

  if (camera?.isOrthographicCamera) {
    const fovRad = THREE.MathUtils.degToRad(VIEWPORT_CAMERA_FOV);
    const frustumHeight = Math.max(
      camera.top - camera.bottom,
      typeof window !== 'undefined' ? window.innerHeight : 1,
      1
    );
    const matchedZoom =
      frustumHeight / (2 * distance * Math.tan(fovRad / 2));
    const zoom = Math.max(camera.zoom || 1, 0.001);
    scale *= matchedZoom / zoom;
  }

  return scale;
}

const _boundsBox = new THREE.Box3();
const _boundsMeshBox = new THREE.Box3();
const _boundsLocalBox = new THREE.Box3();
const _boundsCenter = new Vector3();
const _boundsSize = new Vector3();
const _boundsCorner = new Vector3();
const _boundsInvMatrix = new Matrix4();
const _boundsMeshMatrix = new Matrix4();
const _boundsWorldQuat = new Quaternion();
const _boundsWorldScale = new Vector3();
const _boundsWorldPos = new Vector3();
const _boundsRotMatrix = new Matrix4();
const _boundsInvRotMatrix = new Matrix4();
const _boundsOffsetMatrix = new Matrix4();

/** Opposite corner index for the local OBB corner order used by the selection box. */
const BBOX_CORNER_OPPOSITE = [6, 7, 4, 5, 2, 3, 0, 1];

const _bboxPlane = new THREE.Plane();
const _bboxAnchorWorld = new Vector3();
const _bboxAnchorWorldStart = new Vector3();
const _bboxDragStartWorld = new Vector3();
const _bboxPointerWorld = new Vector3();
const _bboxLocalStart = new Vector3();
const _bboxLocalNow = new Vector3();
const _bboxScaleFactors = new Vector3();
const _bboxParentWorld = new Vector3();
const _bboxPosDelta = new Vector3();
const _cornerWorldPos = new Vector3();
const _uniformHandleScale = new Vector3();

function isTransformLightObject(object) {
  if (!object) return false;
  return (
    object.isLight ||
    object.type === 'AmbientLight' ||
    object.type === 'DirectionalLight' ||
    object.type === 'PointLight' ||
    object.type === 'SpotLight' ||
    object.type === 'RectAreaLight' ||
    object.type === 'AmbientLightHelper' ||
    object.type === 'DirectionalLightHelper' ||
    object.type === 'PointLightHelper' ||
    object.type === 'SpotLightHelper' ||
    object.type === 'AreaLightHelper'
  );
}

/** True when the selected node is an asset parent (GLB pivot, shape group, multi-mesh group). */
function isParentAssetSelection(object) {
  if (!object) return false;
  if (object.isMesh || object.isSkinnedMesh) return false;

  const glbType = object.glb_type ?? object.userData?.glb_type;
  if (glbType === 'ply') return false;

  if (object.deleteType === 'mesh' && object.objType === '3d') return true;
  if (object.isShapes || glbType === 'shapes' || object.userData?.isShapes) {
    return true;
  }

  if (object.userData?.isHotspot) return true;

  if (object.type === 'Group' && object.parent?.type !== 'Scene') {
    let meshCount = 0;
    forEachOwnAssetMesh(object, () => {
      meshCount += 1;
    });
    if (meshCount > 1) return true;
  }

  return false;
}

/**
 * Nested hierarchy assets (other meshes/images/etc parented under this object)
 * — used to decide whether the selection OBB should expand to children.
 */
function isIndependentSceneAsset(node) {
  if (!node) return false;
  if (node.isLight) return true;
  if (node.userData?.isHotspot || node.deleteType === 'hotspot') return true;
  if (node.userData?.particleId || node.deleteType === 'particle') return true;
  if (node.isShapes || node.userData?.isShapes) return true;
  const dt = node.deleteType;
  if (
    dt &&
    ['mesh', 'image', 'video', 'audio', 'text', 'hotspot', 'empty'].includes(dt)
  ) {
    return true;
  }
  return false;
}

/** Visual child assets that should enlarge the parent's selection bounding box. */
function isHierarchyBoundsChildAsset(node) {
  if (!node || node.isLight) return false;
  if (node.userData?.isHotspot || node.deleteType === 'hotspot') return false;
  if (node.userData?.particleId || node.deleteType === 'particle') return false;
  if (node.userData?.isTransformHandle || node.userData?.selectionBoxHandle) {
    return false;
  }
  if (node.userData?.isHdriBackground || node.name === 'hdriBackgroundSphere') {
    return false;
  }
  if (node.isShapes || node.userData?.isShapes) return true;
  const dt = node.deleteType;
  return !!(
    dt &&
    ['mesh', 'image', 'video', 'audio', 'text', 'empty'].includes(dt)
  );
}

function hasNestedHierarchyAssets(root) {
  if (!root?.traverse) return false;
  let found = false;
  root.traverse((node) => {
    if (found || node === root) return;
    if (isHierarchyBoundsChildAsset(node)) found = true;
  });
  return found;
}

/** Nodes that must not contribute to (or be walked for) parent selection bounds. */
function shouldSkipFromParentBounds(node) {
  if (!node) return true;
  if (node.isLight) return true;
  if (node.isCamera) return true;
  if (node.type === 'CameraHelper' || node.isSkeletonHelper) return true;
  if (node.userData?.isHotspot || node.deleteType === 'hotspot') return true;
  if (node.userData?.particleId || node.deleteType === 'particle') return true;
  if (node.userData?.isTransformHandle || node.userData?.selectionBoxHandle) {
    return true;
  }
  if (node.userData?.isHdriBackground || node.name === 'hdriBackgroundSphere') {
    return true;
  }
  if (node.name === 'transformControls' || node.name === 'bboxCornerHandles') {
    return true;
  }
  return false;
}

/** Walk meshes that belong to this asset only (skip hierarchy-nested assets). */
function forEachOwnAssetMesh(root, callback) {
  if (!root) return;

  const visit = (node) => {
    if (!node) return;
    if (node !== root && isIndependentSceneAsset(node)) {
      return;
    }
    if (
      (node.isMesh || node.isSkinnedMesh) &&
      !node.userData?.nonSelectable
    ) {
      callback(node);
    }
    const children = node.children;
    if (!children || children.length === 0) return;
    for (let i = 0; i < children.length; i += 1) {
      visit(children[i]);
    }
  };

  visit(root);
}

/**
 * Walk every mesh under the selection for the OBB, including hierarchy
 * child assets (so a parent box covers its children).
 */
function forEachSelectionBoundsMesh(root, callback) {
  if (!root) return;

  const visit = (node) => {
    if (!node) return;
    if (node !== root && shouldSkipFromParentBounds(node)) {
      return;
    }
    if (
      (node.isMesh || node.isSkinnedMesh) &&
      !node.userData?.nonSelectable &&
      !node.userData?.isTransformHandle
    ) {
      callback(node);
    }
    const children = node.children;
    if (!children || children.length === 0) return;
    for (let i = 0; i < children.length; i += 1) {
      visit(children[i]);
    }
  };

  visit(root);
}

/**
 * Parent selection → asset root for multi-mesh assets.
 * Single visual mesh → that mesh (so the OBB hugs mesh edges like Spline).
 */
function resolveSelectionBoundsRoot(object) {
  if (!object) return null;
  if (isParentAssetSelection(object)) return object;
  return object;
}

/**
 * Frame that defines selection-box axes.
 * When the selection has hierarchy child assets, use the parent object so the
 * OBB can cover those children. Otherwise prefer a single visual mesh so the
 * wire box stays flush with geometry.
 */
function resolveSelectionBoxFrame(object) {
  if (!object) return null;

  if (object.userData?.isMultiSelectionPivot) {
    return object;
  }

  // Parent with nested assets → box in parent space covering children.
  if (hasNestedHierarchyAssets(object)) {
    return object;
  }

  if (object.isMesh || object.isSkinnedMesh) {
    return object;
  }

  const ownMeshes = [];
  forEachOwnAssetMesh(object, (mesh) => {
    ownMeshes.push(mesh);
  });

  if (ownMeshes.length === 1) {
    return ownMeshes[0];
  }

  return resolveSelectionBoundsRoot(object) || object;
}

function getMaterialHighlightMeshes() {
  return (store.getState()?.assets_object?.materialHighlightMeshes ?? []).filter(
    (mesh) => mesh && (mesh.isMesh || mesh.isSkinnedMesh)
  );
}

function isLiveObject3D(node) {
  return !!(node && typeof node.updateMatrixWorld === 'function');
}

function isSplatObject(object) {
  return (
    object?.glb_type === 'ply' || object?.userData?.glb_type === 'ply'
  );
}

function setFallbackLocalBoundsBox(size = 1) {
  const half = size / 2;
  _boundsLocalBox.makeEmpty();
  _boundsLocalBox.set(
    new Vector3(-half, -half, -half),
    new Vector3(half, half, half)
  );
  return _boundsLocalBox;
}

function disableMeshShadows(root) {
  root?.traverse?.((child) => {
    if (child.isMesh) {
      child.castShadow = false;
      child.receiveShadow = false;
    }
  });
}

class TransformControls extends Object3D {
  constructor(camera, domElement) {
    super();

    if (domElement === undefined) {
      console.warn(
        'THREE.TransformControls: The second parameter "domElement" is now mandatory.'
      );
      domElement = document;
    }

    this.isTransformControls = true;

    this.visible = false;
    this.domElement = domElement;
    this.domElement.style.touchAction = 'none';

    const _gizmo = new TransformControlsGizmo();
    this._gizmo = _gizmo;
    this.add(_gizmo);

    const _plane = new TransformControlsPlane();
    this._plane = _plane;
    this.add(_plane);

    disableMeshShadows(this);

    this._boxGroup = new Group();
    this._boxGroup.visible = false;
    // Driven by full matrixWorld of the visual mesh — not TRS decompose.
    this._boxGroup.matrixAutoUpdate = false;
    this.add(this._boxGroup);
    // Corner handles live outside _boxGroup so object non-uniform scale
    // never stretches the spheres into ellipsoids.
    this._cornerHandleGroup = new Group();
    this._cornerHandleGroup.visible = false;
    this._cornerHandleGroup.name = 'bboxCornerHandles';
    this.add(this._cornerHandleGroup);
    this._boxMesh = null;
    this._points = null;
    this._lines = null;
    this._isSyncingSelectionBox = false;
    this._selectionBoxFrame = null;

    this._angleDisplay = document.createElement('div');
    this._angleDisplay.style.position = 'absolute';
    this._angleDisplay.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
    this._angleDisplay.style.color = '#ffffff';
    this._angleDisplay.style.padding = '6px 12px';
    this._angleDisplay.style.borderRadius = '10px';
    this._angleDisplay.style.fontFamily = 'Arial, sans-serif';
    this._angleDisplay.style.fontSize = '13px';
    this._angleDisplay.style.pointerEvents = 'none';
    this._angleDisplay.style.display = 'none';
    this._angleDisplay.style.zIndex = '1000';
    this.domElement.ownerDocument.body.appendChild(this._angleDisplay);

    this.hoveredPartName = null;

    const scope = this;

    this._unscaledBoxSize = new Vector3();
    this._unscaledBoxCenter = new Vector3();
    this._attachedObjectGeometryId = null;
    this._lastBoxRecomputeTime = 0;
    this._lastObjectId = null;
    this._lastBoundsRootId = null;
    this._lastQuaternion = new Quaternion();
    this._lastPosition = new Vector3();
    this._lastScale = new Vector3();
    this._originalGizmoColors = {};

    function defineProperty(propName, defaultValue) {
      let propValue = defaultValue;
      Object.defineProperty(scope, propName, {
        get: () => (propValue !== undefined ? propValue : defaultValue),
        set: (value) => {
          if (propValue !== value) {
            propValue = value;
            _plane[propName] = value;
            _gizmo[propName] = value;
            scope.dispatchEvent({ type: propName + '-changed', value });
            scope.dispatchEvent(_changeEvent);
          }
        },
      });
      scope[propName] = defaultValue;
      _plane[propName] = defaultValue;
      _gizmo[propName] = defaultValue;
    }

    defineProperty('camera', camera);
    defineProperty('object', undefined);
    defineProperty('enabled', true);
    defineProperty('axis', null);
    defineProperty('mode', 'translate');
    defineProperty('translationSnap', null);
    defineProperty('rotationSnap', null);
    defineProperty('scaleSnap', null);
    defineProperty('space', 'world');
    defineProperty('size', 1.3);
    defineProperty('dragging', false);
    defineProperty('showX', true);
    defineProperty('showY', true);
    defineProperty('showZ', true);
    defineProperty('showSelectionBox', true);

    const worldPosition = new Vector3();
    const worldPositionStart = new Vector3();
    const worldQuaternion = new Quaternion();
    const worldQuaternionStart = new Quaternion();
    const cameraPosition = new Vector3();
    const cameraQuaternion = new Quaternion();
    const pointStart = new Vector3();
    const pointEnd = new Vector3();
    const rotationAxis = new Vector3();
    const rotationAngle = 0;
    const eye = new Vector3();

    defineProperty('worldPosition', worldPosition);
    defineProperty('worldPositionStart', worldPositionStart);
    defineProperty('worldQuaternion', worldQuaternion);
    defineProperty('worldQuaternionStart', worldQuaternionStart);
    defineProperty('cameraPosition', cameraPosition);
    defineProperty('cameraQuaternion', cameraQuaternion);
    defineProperty('pointStart', pointStart);
    defineProperty('pointEnd', pointEnd);
    defineProperty('rotationAxis', rotationAxis);
    defineProperty('rotationAngle', rotationAngle);
    defineProperty('eye', eye);

    this._offset = new Vector3();
    this._startNorm = new Vector3();
    this._endNorm = new Vector3();
    this._cameraScale = new Vector3();

    this._parentPosition = new Vector3();
    this._parentQuaternion = new Quaternion();
    this._parentQuaternionInv = new Quaternion();
    this._parentScale = new Vector3();

    this._worldScaleStart = new Vector3();
    this._worldQuaternionInv = new Quaternion();
    this._worldScale = new Vector3();

    this._positionStart = new Vector3();
    this._quaternionStart = new Quaternion();
    this._scaleStart = new Vector3();

    // Bounding-box (Spline-style) scale gesture state
    this._bboxScaling = false;
    this._bboxHandleType = null;
    this._bboxHandleIndex = -1;
    this._bboxScaleAxes = 'XYZ';

    this._getPointer = getPointer.bind(this);
    this._onPointerDown = onPointerDown.bind(this);
    this._onPointerHover = onPointerHover.bind(this);
    this._onPointerMove = onPointerMove.bind(this);
    this._onPointerUp = onPointerUp.bind(this);

    this.domElement.addEventListener('pointerdown', this._onPointerDown);
    this.domElement.addEventListener('pointermove', this._onPointerHover);
    this.domElement.addEventListener('pointerup', this._onPointerUp);

    // Save original colors
    this._saveOriginalColors();
  }

  _saveOriginalColors() {
    this._originalGizmoColors = {};
    this._gizmo.gizmo.children.forEach((child) => {
      if (child.name && child.material && child.material.color) {
        this._originalGizmoColors[child.name] = child.material.color.getHex();
      }
    });
  }

  _updateGizmoHighlight() {
    if (!this._gizmo?.gizmo) return;

    const isDragging = this.dragging;

    // Reset all to original colors (protect angle parts)
    this._gizmo.gizmo.children.forEach((child) => {
      if (child.tag === 'angleNeedle' || child.tag === 'angleArc') return;

      if (child.name && this._originalGizmoColors[child.name] !== undefined) {
        child.material.color.setHex(this._originalGizmoColors[child.name]);
      }
    });

    // Hover highlight → only the exact hovered part
    if (!isDragging && this.hoveredPartName) {
      this._gizmo.gizmo.children.forEach((child) => {
        if (child.tag === 'angleNeedle' || child.tag === 'angleArc') return;
        if (child.name === this.hoveredPartName && child.material) {
          child.material.color.setHex(0xffff00); // yellow on hover
        }
      });
    }

    // Dragging highlight → whole axis
    if (isDragging && this.axis && !this._bboxScaling) {
      this._gizmo.gizmo.children.forEach((child) => {
        if (child.tag === 'angleNeedle' || child.tag === 'angleArc') return;
        if (child.name?.startsWith(this.axis + '-') && child.material) {
          child.material.color.setHex(0xffee33); // brighter when dragging
        }
      });
    }

    this._updateBoxHandleHighlight();
  }

  _updateBoxHandleHighlight() {
    if (!this._points) return;

    const isDragging = this.dragging && this._bboxScaling;
    this._points.forEach((handle) => {
      if (!handle?.material?.color) return;
      const hovered =
        !isDragging && this.hoveredPartName && handle.name === this.hoveredPartName;
      const activeDrag =
        isDragging &&
        this._bboxHandleType === 'corner' &&
        handle.userData.cornerIndex === this._bboxHandleIndex;
      handle.material.color.setHex(
        hovered || activeDrag ? 0xffff00 : 0x5c5c5c
      );
    });
  }

  _createPointLineGeometry() {
    // Clear existing elements
    if (this._boxMesh) {
      this._boxGroup.remove(this._boxMesh);
      this._boxMesh.geometry?.dispose();
      this._boxMesh.material?.dispose();
      this._boxMesh = null;
    }
    if (this._points) {
      this._points.forEach((p) => {
        this._cornerHandleGroup.remove(p);
        p.geometry?.dispose();
        p.material?.dispose();
      });
      this._points = null;
    }
    if (this._lines) {
      this._boxGroup.remove(this._lines);
      this._lines.geometry?.dispose();
      this._lines.material?.dispose();
      this._lines = null;
    }

    const sizeX = Number.isFinite(this._unscaledBoxSize.x)
      ? Math.max(Math.abs(this._unscaledBoxSize.x), 0.001)
      : 0.001;
    const sizeY = Number.isFinite(this._unscaledBoxSize.y)
      ? Math.max(Math.abs(this._unscaledBoxSize.y), 0.001)
      : 0.001;
    const sizeZ = Number.isFinite(this._unscaledBoxSize.z)
      ? Math.max(Math.abs(this._unscaledBoxSize.z), 0.001)
      : 0.001;

    // Invisible volume (hit / bounds reference) — wireframe + handles provide the look
    const boxGeometry = new BoxGeometry(sizeX, sizeY, sizeZ);
    const boxMaterial = new MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      depthTest: false,
      depthWrite: false,
      side: DoubleSide,
    });
    this._boxMesh = new Mesh(boxGeometry, boxMaterial);
    this._boxMesh.castShadow = false;
    this._boxMesh.receiveShadow = false;
    this._boxMesh.raycast = () => {};
    this._boxGroup.add(this._boxMesh);

    const halfX = sizeX / 2;
    const halfY = sizeY / 2;
    const halfZ = sizeZ / 2;

    // Local OBB corners (Spline-style oriented box)
    const corners = [
      [-halfX, -halfY, -halfZ],
      [halfX, -halfY, -halfZ],
      [halfX, halfY, -halfZ],
      [-halfX, halfY, -halfZ],
      [-halfX, -halfY, halfZ],
      [halfX, -halfY, halfZ],
      [halfX, halfY, halfZ],
      [-halfX, halfY, halfZ],
    ];

    const edges = [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 0], // bottom
      [4, 5],
      [5, 6],
      [6, 7],
      [7, 4], // top
      [0, 4],
      [1, 5],
      [2, 6],
      [3, 7], // pillars
    ];

    // Thin medium-grey wireframe (Spline)
    const linePositions = [];
    edges.forEach(([a, b]) => {
      const [x1, y1, z1] = corners[a];
      const [x2, y2, z2] = corners[b];
      linePositions.push(x1, y1, z1, x2, y2, z2);
    });

    const lineGeometry = new BufferGeometry();
    lineGeometry.setAttribute(
      'position',
      new Float32BufferAttribute(linePositions, 3)
    );
    const lineMaterial = new LineBasicMaterial({
      color: 0x9a9a9a,
      depthTest: false,
      depthWrite: false,
    });
    this._lines = new LineSegments(lineGeometry, lineMaterial);
    this._lines.raycast = () => {};
    this._boxGroup.add(this._lines);

    // Corner spheres only — parented outside _boxGroup so they never stretch
    const cornerGeo = new SphereGeometry(0.035, 18, 14);
    const handleMat = new MeshBasicMaterial({
      color: 0x5c5c5c,
      depthTest: false,
      depthWrite: false,
    });

    this._points = [];

    corners.forEach(([x, y, z], i) => {
      const handle = new Mesh(cornerGeo.clone(), handleMat.clone());
      handle.name = `bbox-corner-${i}`;
      handle.castShadow = false;
      handle.receiveShadow = false;
      handle.position.set(x, y, z);
      handle.userData.selectionBoxHandle = 'corner';
      handle.userData.cornerIndex = i;
      handle.userData.scaleAxes = 'XYZ';
      handle.userData.oppositeCornerIndex = BBOX_CORNER_OPPOSITE[i];
      handle.userData.localCorner = new Vector3(x, y, z);
      handle.userData.nonSelectable = true;
      handle.userData.isTransformHandle = true;
      this._cornerHandleGroup.add(handle);
      this._points.push(handle);
    });

    cornerGeo.dispose();
    handleMat.dispose();
    disableMeshShadows(this._boxGroup);
    disableMeshShadows(this._cornerHandleGroup);
  }

  /**
   * Expand `_boundsLocalBox` with mesh geometry corners expressed in `frame`
   * local space (via inv(frame.matrixWorld)).
   */
  _expandMeshInFrameLocal(mesh, invFrameMatrix) {
    if (!mesh) return false;

    const expandLocalPoint = (worldPoint) => {
      _boundsCorner.copy(worldPoint).applyMatrix4(invFrameMatrix);
      _boundsLocalBox.expandByPoint(_boundsCorner);
    };

    if (mesh.isSkinnedMesh) {
      _boundsMeshBox.setFromObject(mesh);
      if (_boundsMeshBox.isEmpty()) return false;
      const { min, max } = _boundsMeshBox;
      for (const x of [min.x, max.x]) {
        for (const y of [min.y, max.y]) {
          for (const z of [min.z, max.z]) {
            expandLocalPoint(_tempVector.set(x, y, z));
          }
        }
      }
      return true;
    }

    if (!mesh.geometry) return false;
    const geometry = mesh.geometry;
    if (!geometry.boundingBox) geometry.computeBoundingBox();
    let geoBox = geometry.boundingBox;
    if (!geoBox || geoBox.isEmpty()) {
      geometry.computeBoundingBox();
      geoBox = geometry.boundingBox;
    }
    if (!geoBox || geoBox.isEmpty()) return false;

    mesh.updateWorldMatrix(true, false);

    // Fast path: frame IS this mesh → geometry BB is already the tight OBB.
    if (mesh === this._selectionBoxFrame) {
      _boundsLocalBox.copy(geoBox);
      return true;
    }

    const { min, max } = geoBox;
    for (const x of [min.x, max.x]) {
      for (const y of [min.y, max.y]) {
        for (const z of [min.z, max.z]) {
          _boundsCorner.set(x, y, z).applyMatrix4(mesh.matrixWorld);
          expandLocalPoint(_boundsCorner);
        }
      }
    }
    return true;
  }

  _collectLocalBoundsBox(object, frame) {
    _boundsLocalBox.makeEmpty();
    if (!frame) return _boundsLocalBox;

    const multiObjects = object?.userData?.isMultiSelectionPivot
      ? object.userData.multiSelectionObjects
      : null;

    if (!isLiveObject3D(frame) && !multiObjects?.length) {
      return setFallbackLocalBoundsBox(isSplatObject(object) ? 2 : 1);
    }

    if (isLiveObject3D(frame)) {
      frame.updateMatrixWorld(true);
    }

    _boundsInvMatrix.copy(frame.matrixWorld).invert();

    const collectMesh = (mesh) => {
      if (!this._expandMeshInFrameLocal(mesh, _boundsInvMatrix)) {
        _boundsBox.setFromObject(mesh);
        if (_boundsBox.isEmpty()) return;
        const { min, max } = _boundsBox;
        for (const x of [min.x, max.x]) {
          for (const y of [min.y, max.y]) {
            for (const z of [min.z, max.z]) {
              _boundsCorner.set(x, y, z).applyMatrix4(_boundsInvMatrix);
              _boundsLocalBox.expandByPoint(_boundsCorner);
            }
          }
        }
      }
    };

    if (multiObjects?.length) {
      multiObjects.forEach((obj) => {
        if (!obj) return;
        obj.updateMatrixWorld(true);
        if (obj.isMesh || obj.isSkinnedMesh) {
          collectMesh(obj);
        } else {
          forEachSelectionBoundsMesh(obj, collectMesh);
        }
      });
      return _boundsLocalBox;
    }

    const materialMeshes = getMaterialHighlightMeshes();
    if (
      materialMeshes.length > 0 &&
      isParentAssetSelection(object) &&
      !object?.userData?.isMultiSelectionPivot
    ) {
      materialMeshes.forEach((mesh) => collectMesh(mesh));
      if (!_boundsLocalBox.isEmpty()) return _boundsLocalBox;
    }

    // Selection OBB includes hierarchy child assets under the selected parent.
    const boundsRoot =
      object && isLiveObject3D(object) ? object : frame;
    forEachSelectionBoundsMesh(boundsRoot, collectMesh);

    if (_boundsLocalBox.isEmpty()) {
      _boundsBox.setFromObject(frame);
      if (!_boundsBox.isEmpty()) {
        const { min, max } = _boundsBox;
        for (const x of [min.x, max.x]) {
          for (const y of [min.y, max.y]) {
            for (const z of [min.z, max.z]) {
              _boundsCorner.set(x, y, z).applyMatrix4(_boundsInvMatrix);
              _boundsLocalBox.expandByPoint(_boundsCorner);
            }
          }
        }
      } else {
        return setFallbackLocalBoundsBox(isSplatObject(object) ? 2 : 1);
      }
    }

    return _boundsLocalBox;
  }

  _refreshSelectionBoxGeometry() {
    if (!this.object || !this.showSelectionBox) return;

    this.object.updateMatrixWorld(true);
    this._unscaledBoxSize.copy(this._computeUnscaledBoundingBox(this.object));
    this._createPointLineGeometry();
    this._syncSelectionBoxTransform();
    this._updateSpheresScale();
    this._boxGroup.updateMatrixWorld(true);
  }

  /**
   * Size/center of the selection OBB in the visual-frame local space.
   */
  _computeUnscaledBoundingBox(object) {
    if (!object) {
      this._unscaledBoxCenter.set(0, 0, 0);
      this._selectionBoxFrame = null;
      return new Vector3(0.001, 0.001, 0.001);
    }

    const frame = resolveSelectionBoxFrame(object) || object;
    this._selectionBoxFrame = frame;
    this._collectLocalBoundsBox(object, frame);

    _boundsLocalBox.getSize(_boundsSize);
    _boundsLocalBox.getCenter(_boundsCenter);
    this._unscaledBoxCenter.copy(_boundsCenter);

    _boundsSize.x = Number.isFinite(_boundsSize.x)
      ? Math.max(Math.abs(_boundsSize.x), 0.001)
      : 0.001;
    _boundsSize.y = Number.isFinite(_boundsSize.y)
      ? Math.max(Math.abs(_boundsSize.y), 0.001)
      : 0.001;
    _boundsSize.z = Number.isFinite(_boundsSize.z)
      ? Math.max(Math.abs(_boundsSize.z), 0.001)
      : 0.001;

    return _boundsSize.clone();
  }

  /**
   * Stick box to the visual mesh/group using full matrixWorld (Spline-style).
   * Avoids TRS decompose so edges stay flush even with hierarchy / non-uniform scale.
   */
  _syncSelectionBoxTransform() {
    if (!this.object || this._isSyncingSelectionBox) return;

    const frame =
      this._selectionBoxFrame ||
      resolveSelectionBoxFrame(this.object) ||
      this.object;
    if (!isLiveObject3D(frame)) {
      if (!isLiveObject3D(this.object)) return;
      this._boxGroup.matrixAutoUpdate = true;
      this._boxGroup.position.copy(this.object.position);
      this._boxGroup.quaternion.copy(this.object.quaternion);
      this._boxGroup.scale.set(1, 1, 1);
      return;
    }

    this._isSyncingSelectionBox = true;
    try {
      if (frame !== this && !this._isAncestorOf(frame)) {
        frame.updateMatrixWorld(true);
      }

      // boxMatrix = frame.matrixWorld * Translate(localCenter)
      // Geometry already has local extents → never decompose/reapply scale.
      if (!frame.matrixWorld?.elements) {
        frame.updateMatrixWorld?.(true);
      }
      if (!frame.matrixWorld?.elements) {
        this._boxGroup.matrixAutoUpdate = true;
        this._boxGroup.position.copy(frame.position);
        this._boxGroup.quaternion.copy(frame.quaternion);
        this._boxGroup.scale.set(1, 1, 1);
        return;
      }
      _boundsOffsetMatrix.makeTranslation(
        this._unscaledBoxCenter.x,
        this._unscaledBoxCenter.y,
        this._unscaledBoxCenter.z
      );
      this._boxGroup.matrixAutoUpdate = false;
      this._boxGroup.matrix.multiplyMatrices(
        frame.matrixWorld,
        _boundsOffsetMatrix
      );
      this._boxGroup.matrixWorldNeedsUpdate = true;
    } finally {
      this._isSyncingSelectionBox = false;
    }
  }

  _isAncestorOf(node) {
    let p = node?.parent;
    while (p) {
      if (p === this) return true;
      p = p.parent;
    }
    return false;
  }

  /** Keep existing corner/line geometry; only stick transform to the object. */
  _stickSelectionBoxToObject() {
    if (!this.object || !this.showSelectionBox || this._isSyncingSelectionBox) {
      return;
    }
    this._syncSelectionBoxTransform();
    this._updateSpheresScale();
    this._boxGroup.visible = true;
    if (this._cornerHandleGroup) this._cornerHandleGroup.visible = true;
  }

  updateAngleDisplay() {
    if (
      this.mode !== 'rotate' ||
      !this.dragging ||
      !this.axis ||
      this.axis === 'E' ||
      this.axis === 'XYZE' ||
      !this.object
    ) {
      this._angleDisplay.style.display = 'none';
      return;
    }

    const deg = ((this.rotationAngle * 180) / Math.PI).toFixed(1);
    this._angleDisplay.textContent = `${deg}°`;

    const meter = this._gizmo.gizmo.children.find(
      (c) => c.tag === 'angleMeter'
    );
    if (!meter) {
      this._angleDisplay.style.display = 'none';
      return;
    }

    const pos = new Vector3();
    meter.getWorldPosition(pos);
    pos.add(new Vector3(0, 0.9, 0.9));
    pos.project(this.camera);

    const rect = this.domElement.getBoundingClientRect();
    const x = ((pos.x + 1) / 2) * rect.width + rect.left;
    const y = ((-pos.y + 1) / 2) * rect.height + rect.top;

    this._angleDisplay.style.left = `${x - this._angleDisplay.offsetWidth / 2}px`;
    this._angleDisplay.style.top = `${y - this._angleDisplay.offsetHeight - 14}px`;
    this._angleDisplay.style.display = 'block';
  }

  updateMatrixWorld() {
    if (this.object) {
      if (!this._isAttachedObjectVisible()) {
        this.visible = false;
        this._boxGroup.visible = false;
        if (this._cornerHandleGroup) this._cornerHandleGroup.visible = false;
        this._angleDisplay.style.display = 'none';
        this.camera.updateMatrixWorld();
        super.updateMatrixWorld();
        return;
      }

      this.visible = true;

      // Full parent-chain update so hierarchical TRS is reflected in the box.
      this.object.updateMatrixWorld(true);
      if (this.object.parent) {
        this.object.parent.matrixWorld.decompose(
          this._parentPosition,
          this._parentQuaternion,
          this._parentScale
        );
      } else {
        this._parentPosition.set(0, 0, 0);
        this._parentQuaternion.identity();
        this._parentScale.set(1, 1, 1);
      }

      this.object.matrixWorld.decompose(
        this.worldPosition,
        this.worldQuaternion,
        this._worldScale
      );
      this._parentQuaternionInv.copy(this._parentQuaternion).invert();
      this._worldQuaternionInv.copy(this.worldQuaternion).invert();

      // Check if it's a light or light helper
      const isLight =
        this.object.isLight ||
        this.object.type === 'AmbientLight' ||
        this.object.type === 'DirectionalLight' ||
        this.object.type === 'PointLight' ||
        this.object.type === 'SpotLight' ||
        this.object.type === 'RectAreaLight' ||
        this.object.type === 'AmbientLightHelper' ||
        this.object.type === 'DirectionalLightHelper' ||
        this.object.type === 'PointLightHelper' ||
        this.object.type === 'SpotLightHelper' ||
        this.object.type === 'AreaLightHelper';

      const geoId = this.object.geometry?.id;
      const objId = this.object.id;
      const boundsRoot = resolveSelectionBoxFrame(this.object) || this.object;
      const boundsRootId = boundsRoot.uuid;
      // Track world pose so parent-driven hierarchy changes also refresh the box.
      const q = this.worldQuaternion;
      const p = this.worldPosition;
      const s = this._worldScale;

      const isParticle = this.object.deleteType === 'particle';

      const scaleChanged =
        !this._lastScale ||
        Math.abs(this._lastScale.x - s.x) > 1e-6 ||
        Math.abs(this._lastScale.y - s.y) > 1e-6 ||
        Math.abs(this._lastScale.z - s.z) > 1e-6;

      const transformChanged =
        this._attachedObjectGeometryId !== geoId ||
        this._lastObjectId !== objId ||
        this._lastBoundsRootId !== boundsRootId ||
        !this._lastQuaternion?.equals(q) ||
        !this._lastPosition?.equals(p) ||
        scaleChanged;

      const hasGeometryChange =
        this._attachedObjectGeometryId !== geoId ||
        this._lastObjectId !== objId ||
        this._lastBoundsRootId !== boundsRootId ||
        this.object.userData?.forceBoxUpdate;

      const throttleMs = 300;
      const now = Date.now();
      const allowRecompute =
        transformChanged && now - this._lastBoxRecomputeTime > throttleMs;

      // Local-extent OBB: scale rides on _boxGroup — only rebuild when geometry/structure changes.
      const shouldRecomputeBox =
        this.showSelectionBox &&
        !isLight &&
        (hasGeometryChange ||
          (allowRecompute && !isParticle && this.dragging && scaleChanged));

      if (shouldRecomputeBox && hasGeometryChange) {
        if (this.object.userData?.forceBoxUpdate) {
          // Redux/Immer may freeze userData — never assign in place on frozen bags.
          try {
            if (!Object.isExtensible(this.object.userData)) {
              this.object.userData = {
                ...this.object.userData,
                forceBoxUpdate: false,
              };
            } else {
              this.object.userData.forceBoxUpdate = false;
            }
          } catch {
            this.object.userData = {
              ...(this.object.userData || {}),
              forceBoxUpdate: false,
            };
          }
        }
        this._lastBoxRecomputeTime = now;
        this._unscaledBoxSize.copy(
          this._computeUnscaledBoundingBox(this.object)
        );
        this._attachedObjectGeometryId = geoId;
        this._lastObjectId = objId;
        this._lastBoundsRootId = boundsRootId;
        this._lastQuaternion = q.clone();
        this._lastPosition = p.clone();
        this._lastScale = s.clone();
        this._createPointLineGeometry();
      } else if (transformChanged) {
        this._attachedObjectGeometryId = geoId;
        this._lastObjectId = objId;
        this._lastBoundsRootId = boundsRootId;
        this._lastQuaternion.copy(q);
        this._lastPosition.copy(p);
        this._lastScale.copy(s);
      }

      if (this.showSelectionBox && !isLight) {
        this._stickSelectionBoxToObject();
      } else {
        this._boxGroup.visible = false;
        if (this._cornerHandleGroup) this._cornerHandleGroup.visible = false;
      }
    }

    try {
      this.camera.updateMatrixWorld();
      this.camera.matrixWorld.decompose(
        this.cameraPosition,
        this.cameraQuaternion,
        this._cameraScale
      );

      if (this.camera.isOrthographicCamera) {
        this.camera.getWorldDirection(this.eye).negate();
      } else {
        this.eye.copy(this.cameraPosition).sub(this.worldPosition).normalize();
      }
    } catch (_camErr) {
      // camera update failed — gizmo scale may be off but position will still be applied
    }

    super.updateMatrixWorld();

    this.updateAngleDisplay();
  }

  pointerHover(pointer) {
    if (!this.object || this.dragging) return;

    _raycaster.setFromCamera(pointer, this.camera);
    const gizmoHit = intersectObjectWithRay(this._gizmo.picker, _raycaster);
    const boxHit = this._intersectSelectionBoxHandle(_raycaster);

    const prevHovered = this.hoveredPartName;

    let best = null;
    if (gizmoHit && boxHit) {
      best =
        boxHit.distance < gizmoHit.distance
          ? { type: 'box', hit: boxHit }
          : { type: 'gizmo', hit: gizmoHit };
    } else if (boxHit) {
      best = { type: 'box', hit: boxHit };
    } else if (gizmoHit) {
      best = { type: 'gizmo', hit: gizmoHit };
    }

    if (best?.type === 'box') {
      this.hoveredPartName = best.hit.object.name;
      this.axis = best.hit.object.userData.scaleAxes || 'XYZ';
    } else if (best?.type === 'gizmo') {
      this.hoveredPartName = best.hit.object.name;
      this.axis = this.hoveredPartName.split('-')[0] || null;
    } else {
      this.hoveredPartName = null;
      this.axis = null;
    }

    if (prevHovered !== this.hoveredPartName) {
      this._updateGizmoHighlight();
    }

    this.updateAngleDisplay();
  }

  _intersectSelectionBoxHandle(raycaster) {
    if (
      !this.showSelectionBox ||
      !this._cornerHandleGroup?.visible ||
      !this._points?.length
    ) {
      return false;
    }

    const intersects = raycaster.intersectObjects(this._points, false);
    for (const hit of intersects) {
      if (
        hit.object.visible &&
        hit.object.userData.selectionBoxHandle === 'corner'
      ) {
        return hit;
      }
    }
    return false;
  }

  _getBBoxHandleWorldAnchor(handle) {
    if (!handle || handle.userData.selectionBoxHandle !== 'corner') return null;

    const opposite =
      this._points?.[handle.userData.oppositeCornerIndex] ||
      this._points?.find(
        (p) =>
          p.userData.selectionBoxHandle === 'corner' &&
          p.userData.cornerIndex === handle.userData.oppositeCornerIndex
      );
    return opposite || null;
  }

  pointerDown(pointer) {
    if (!this.object || this.dragging || pointer.button !== 0) return;

    _raycaster.setFromCamera(pointer, this.camera);
    const gizmoHit = intersectObjectWithRay(this._gizmo.picker, _raycaster);
    const boxHit = this._intersectSelectionBoxHandle(_raycaster);

    let useBox = false;
    let intersect = null;

    if (gizmoHit && boxHit) {
      useBox = boxHit.distance < gizmoHit.distance;
      intersect = useBox ? boxHit : gizmoHit;
    } else if (boxHit) {
      useBox = true;
      intersect = boxHit;
    } else if (gizmoHit) {
      intersect = gizmoHit;
    } else {
      return;
    }

    if (useBox) {
      if (isTransformLightObject(this.object)) return;

      const handle = intersect.object;
      this._bboxScaling = true;
      this._bboxHandleType = 'corner';
      this._bboxHandleIndex = handle.userData.cornerIndex;
      this._bboxScaleAxes = 'XYZ';
      this.mode = 'scale';
      this.axis = 'XYZ';
      this.hoveredPartName = handle.name;
      this._updateGizmoHighlight();

      this.object.updateMatrixWorld();
      this.object.parent?.updateMatrixWorld();
      this._stickSelectionBoxToObject();
      this._boxGroup.updateMatrixWorld(true);
      this._cornerHandleGroup?.updateMatrixWorld(true);

      handle.getWorldPosition(_bboxDragStartWorld);

      const anchorHandle = this._getBBoxHandleWorldAnchor(handle);
      if (anchorHandle) {
        anchorHandle.getWorldPosition(_bboxAnchorWorldStart);
      } else {
        this._bboxScaling = false;
        return;
      }

      this.camera.getWorldDirection(_tempVector);
      _bboxPlane.setFromNormalAndCoplanarPoint(
        _tempVector,
        _bboxDragStartWorld
      );

      this._positionStart.copy(this.object.position);
      this._quaternionStart.copy(this.object.quaternion);
      this._scaleStart.copy(this.object.scale);

      this.object.matrixWorld.decompose(
        this.worldPositionStart,
        this.worldQuaternionStart,
        this._worldScaleStart
      );

      // Store start drag vector in object-local axes (from fixed opposite corner).
      _bboxLocalStart
        .copy(_bboxDragStartWorld)
        .sub(_bboxAnchorWorldStart)
        .applyQuaternion(
          _tempQuaternion.copy(this.worldQuaternionStart).invert()
        );

      this.pointStart.copy(_bboxDragStartWorld).sub(this.worldPositionStart);
      this.dragging = true;
      _mouseDownEvent.mode = this.mode;
      this.dispatchEvent(_mouseDownEvent);
      this.updateAngleDisplay();
      return;
    }

    const geo = intersect.object.geometry;

    if (geo instanceof SphereGeometry) {
      this.mode = 'scale';
    } else if (
      geo instanceof LatheGeometry ||
      geo instanceof CylinderGeometry
    ) {
      this.mode = 'translate';
    } else if (geo instanceof TorusGeometry) {
      this.mode = 'rotate';
    } else {
      return;
    }

    this._bboxScaling = false;
    this.axis = intersect.object.name.split('-')[0] || null;
    this._updateGizmoHighlight();

    const planeIntersect = intersectObjectWithRay(
      this._plane,
      _raycaster,
      true
    );
    if (!planeIntersect) {
      this.axis = null;
      this._updateGizmoHighlight();
      return;
    }

    this.object.updateMatrixWorld();
    this.object.parent?.updateMatrixWorld();

    this._positionStart.copy(this.object.position);
    this._quaternionStart.copy(this.object.quaternion);
    this._scaleStart.copy(this.object.scale);

    this.object.matrixWorld.decompose(
      this.worldPositionStart,
      this.worldQuaternionStart,
      this._worldScaleStart
    );

    this.pointStart.copy(planeIntersect.point).sub(this.worldPositionStart);

    if (this.mode === 'rotate') this._gizmo.startAngle = 0;

    this.dragging = true;
    _mouseDownEvent.mode = this.mode;
    this.dispatchEvent(_mouseDownEvent);
    // Stick existing geometry — avoid rebuild lag on drag start.
    this._stickSelectionBoxToObject();

    this.updateAngleDisplay();
  }

  pointerMove(pointer) {
    const axis = this.axis;
    const mode = this.mode;
    const object = this.object;

    if (!object || !axis || !this.dragging || pointer.button !== -1) return;

    _raycaster.setFromCamera(pointer, this.camera);

    if (this._bboxScaling) {
      if (!_raycaster.ray.intersectPlane(_bboxPlane, _bboxPointerWorld)) {
        return;
      }

      _bboxLocalNow
        .copy(_bboxPointerWorld)
        .sub(_bboxAnchorWorldStart)
        .applyQuaternion(
          _tempQuaternion.copy(this.worldQuaternionStart).invert()
        );

      const EPS = 1e-6;
      _bboxScaleFactors.set(1, 1, 1);

      if (axis.includes('X')) {
        if (Math.abs(_bboxLocalStart.x) > EPS) {
          _bboxScaleFactors.x = _bboxLocalNow.x / _bboxLocalStart.x;
        }
      }
      if (axis.includes('Y')) {
        if (Math.abs(_bboxLocalStart.y) > EPS) {
          _bboxScaleFactors.y = _bboxLocalNow.y / _bboxLocalStart.y;
        }
      }
      if (axis.includes('Z')) {
        if (Math.abs(_bboxLocalStart.z) > EPS) {
          _bboxScaleFactors.z = _bboxLocalNow.z / _bboxLocalStart.z;
        }
      }

      // Flip / collapse guards — keep a tiny positive scale.
      _bboxScaleFactors.x =
        Math.sign(_bboxScaleFactors.x) *
          Math.max(Math.abs(_bboxScaleFactors.x), 1e-3) || 1e-3;
      _bboxScaleFactors.y =
        Math.sign(_bboxScaleFactors.y) *
          Math.max(Math.abs(_bboxScaleFactors.y), 1e-3) || 1e-3;
      _bboxScaleFactors.z =
        Math.sign(_bboxScaleFactors.z) *
          Math.max(Math.abs(_bboxScaleFactors.z), 1e-3) || 1e-3;

      if (!isTransformLightObject(object)) {
        object.scale.copy(this._scaleStart).multiply(_bboxScaleFactors);

        if (this.scaleSnap) {
          if (axis.includes('X'))
            object.scale.x =
              Math.round(object.scale.x / this.scaleSnap) * this.scaleSnap ||
              this.scaleSnap;
          if (axis.includes('Y'))
            object.scale.y =
              Math.round(object.scale.y / this.scaleSnap) * this.scaleSnap ||
              this.scaleSnap;
          if (axis.includes('Z'))
            object.scale.z =
              Math.round(object.scale.z / this.scaleSnap) * this.scaleSnap ||
              this.scaleSnap;
        }

        // Keep the opposite corner fixed in world space (Spline behavior).
        object.updateMatrixWorld(true);
        this._stickSelectionBoxToObject();
        this._boxGroup.updateMatrixWorld(true);
        this._cornerHandleGroup?.updateMatrixWorld(true);

        const handle = this._points?.find(
          (p) =>
            p.userData.selectionBoxHandle === 'corner' &&
            p.userData.cornerIndex === this._bboxHandleIndex
        );
        const anchorHandle = this._getBBoxHandleWorldAnchor(handle);
        if (anchorHandle) {
          anchorHandle.getWorldPosition(_bboxAnchorWorld);
        } else {
          _bboxAnchorWorld.copy(_bboxAnchorWorldStart);
        }

        _bboxPosDelta.subVectors(_bboxAnchorWorldStart, _bboxAnchorWorld);
        if (object.parent) {
          object.parent.worldToLocal(_bboxParentWorld.copy(_bboxAnchorWorldStart));
          object.parent.worldToLocal(_tempVector.copy(_bboxAnchorWorld));
          object.position.add(_bboxParentWorld.sub(_tempVector));
        } else {
          object.position.add(_bboxPosDelta);
        }
      }

      this._stickSelectionBoxToObject();
      this.dispatchEvent(_changeEvent);
      this.dispatchEvent(_objectChangeEvent);
      this.updateAngleDisplay();
      return;
    }

    const planeIntersect = intersectObjectWithRay(
      this._plane,
      _raycaster,
      true
    );
    if (!planeIntersect) return;

    this.pointEnd.copy(planeIntersect.point).sub(this.worldPositionStart);

    if (mode === 'translate') {
      this._offset.copy(this.pointEnd).sub(this.pointStart);
      this._offset.applyQuaternion(this._worldQuaternionInv);
      if (axis.indexOf('X') === -1) this._offset.x = 0;
      if (axis.indexOf('Y') === -1) this._offset.y = 0;
      if (axis.indexOf('Z') === -1) this._offset.z = 0;
      this._offset.applyQuaternion(this.worldQuaternion);
      this._offset.divide(this._parentScale);
      object.position.copy(this._positionStart).add(this._offset);

      if (this.translationSnap) {
        object.position.applyQuaternion(
          _tempQuaternion.copy(this.worldQuaternion).invert()
        );
        if (axis.includes('X'))
          object.position.x =
            Math.round(object.position.x / this.translationSnap) *
            this.translationSnap;
        if (axis.includes('Y'))
          object.position.y =
            Math.round(object.position.y / this.translationSnap) *
            this.translationSnap;
        if (axis.includes('Z'))
          object.position.z =
            Math.round(object.position.z / this.translationSnap) *
            this.translationSnap;
        object.position.applyQuaternion(this.worldQuaternion);
      }

      this._stickSelectionBoxToObject();
    } else if (mode === 'scale') {
      // Prevent light assets from being scaled with transform controls
      const isLight = isTransformLightObject(object);

      if (!isLight) {
        if (axis === 'XYZ') {
          let d = this.pointEnd.length() / this.pointStart.length();
          if (this.pointEnd.dot(this.pointStart) < 0) d *= -1;
          d = 1 + (d - 1) * 0.025;
          _tempVector2.set(d, d, d);
        } else {
          _tempVector
            .copy(this.pointStart)
            .applyQuaternion(this._worldQuaternionInv);
          _tempVector2
            .copy(this.pointEnd)
            .applyQuaternion(this._worldQuaternionInv)
            .divide(_tempVector);

          if (!axis.includes('X')) _tempVector2.x = 1;
          if (!axis.includes('Y')) _tempVector2.y = 1;
          if (!axis.includes('Z')) _tempVector2.z = 1;
        }

        object.scale.copy(this._scaleStart).multiply(_tempVector2);

        if (this.scaleSnap) {
          if (axis.includes('X'))
            object.scale.x =
              Math.round(object.scale.x / this.scaleSnap) * this.scaleSnap ||
              this.scaleSnap;
          if (axis.includes('Y'))
            object.scale.y =
              Math.round(object.scale.y / this.scaleSnap) * this.scaleSnap ||
              this.scaleSnap;
          if (axis.includes('Z'))
            object.scale.z =
              Math.round(object.scale.z / this.scaleSnap) * this.scaleSnap ||
              this.scaleSnap;
        }
      }

      this._stickSelectionBoxToObject();
    } else if (mode === 'rotate') {
      this._offset.copy(this.pointEnd).sub(this.pointStart);

      const dist = this.worldPosition.distanceTo(
        _tempVector.setFromMatrixPosition(this.camera.matrixWorld)
      );
      const speed = 20 / dist;

      let inPlane = false;

      if (['X', 'Y', 'Z'].includes(axis)) {
        const unitAxis = _unit[axis];
        this.rotationAxis.copy(unitAxis);
        _tempVector.copy(unitAxis).applyQuaternion(this.worldQuaternionStart);
        _tempVector.cross(this.eye);

        if (_tempVector.lengthSq() < 1e-6) {
          inPlane = true;
        } else {
          this.rotationAngle =
            this._offset.dot(_tempVector.normalize()) * speed;
        }
      } else if (['XY', 'YZ', 'XZ'].includes(axis)) {
        let unitAxis = new Vector3();
        if (axis === 'XY') unitAxis.copy(_unit.Z);
        else if (axis === 'YZ') unitAxis.copy(_unit.X);
        else if (axis === 'XZ') unitAxis.copy(_unit.Y);

        this.rotationAxis.copy(unitAxis);
        _tempVector.copy(unitAxis).applyQuaternion(this.worldQuaternionStart);
        _tempVector.cross(this.eye);

        if (_tempVector.lengthSq() < 1e-6) {
          inPlane = true;
        } else {
          this.rotationAngle =
            this._offset.dot(_tempVector.normalize()) * speed;
        }
      } else if (axis === 'XYZE') {
        this.rotationAxis.copy(this._offset).cross(this.eye).normalize();
        this.rotationAngle =
          this._offset.dot(
            _tempVector.copy(this.rotationAxis).cross(this.eye)
          ) * speed;
      }

      if (axis === 'E' || inPlane) {
        this.rotationAxis.copy(this.eye);
        this.rotationAngle = this.pointEnd.angleTo(this.pointStart);
        this._startNorm.copy(this.pointStart).normalize();
        this._endNorm.copy(this.pointEnd).normalize();
        if (this._endNorm.cross(this._startNorm).dot(this.eye) < 0)
          this.rotationAngle *= -1;
      }

      if (this.rotationSnap) {
        this.rotationAngle =
          Math.round(this.rotationAngle / this.rotationSnap) *
          this.rotationSnap;
      }

      if (['X', 'Y', 'Z'].includes(axis)) {
        const rotQuat = _tempQuaternion.setFromAxisAngle(
          _unit[axis],
          this.rotationAngle
        );
        object.quaternion
          .copy(this._quaternionStart)
          .multiply(rotQuat)
          .normalize();
      } else if (['XY', 'YZ', 'XZ'].includes(axis)) {
        let localAxis;
        if (axis === 'XY') localAxis = _unit.Z;
        else if (axis === 'YZ') localAxis = _unit.X;
        else if (axis === 'XZ') localAxis = _unit.Y;

        const rotQuat = _tempQuaternion.setFromAxisAngle(
          localAxis,
          this.rotationAngle
        );
        object.quaternion
          .copy(this._quaternionStart)
          .multiply(rotQuat)
          .normalize();
      } else {
        this.rotationAxis.applyQuaternion(this._parentQuaternionInv);
        const rotQuat = _tempQuaternion.setFromAxisAngle(
          this.rotationAxis,
          this.rotationAngle
        );
        object.quaternion
          .copy(rotQuat)
          .multiply(this._quaternionStart)
          .normalize();
      }

      object.updateMatrixWorld(true);
      this._stickSelectionBoxToObject();
    }

    this.dispatchEvent(_changeEvent);
    this.dispatchEvent(_objectChangeEvent);
    this.updateAngleDisplay();
  }

  pointerUp(pointer) {
    if (pointer.button !== 0) return;

    if (this.dragging && this.axis) {
      _mouseUpEvent.mode = this.mode;
      this.dispatchEvent(_mouseUpEvent);
      // One solid recompute after the gesture so the box matches final pose.
      this._refreshSelectionBoxGeometry();
    }

    this.dragging = false;
    this._bboxScaling = false;
    this._bboxHandleType = null;
    this._bboxHandleIndex = -1;
    this._bboxScaleAxes = 'XYZ';
    this.hoveredPartName = null;
    this.axis = null;
    this._updateGizmoHighlight();
    this.updateAngleDisplay();
  }

  dispose() {
    this.domElement.removeEventListener('pointerdown', this._onPointerDown);
    this.domElement.removeEventListener('pointermove', this._onPointerHover);
    this.domElement.removeEventListener('pointermove', this._onPointerMove);
    this.domElement.removeEventListener('pointerup', this._onPointerUp);

    this.traverse((child) => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) child.material.dispose();
    });

    if (this._boxGroup) {
      if (this._lines) {
        this._boxGroup.remove(this._lines);
        this._lines.geometry?.dispose();
        this._lines.material?.dispose();
      }
      this.remove(this._boxGroup);
    }
    if (this._cornerHandleGroup) {
      if (this._points) {
        this._points.forEach((p) => {
          this._cornerHandleGroup.remove(p);
          p.geometry?.dispose();
          p.material?.dispose();
        });
      }
      this.remove(this._cornerHandleGroup);
    }

    if (this._angleDisplay) {
      this._angleDisplay.remove();
    }
  }

  _isAttachedObjectVisible() {
    if (!this.object) return false;
    if (this.object.userData?.editorVisible === false) return false;

    const isParticleRoot =
      this.object.deleteType === 'particle' ||
      [
        'foliage',
        'clouds',
        'rain',
        'snow',
        'fire',
        'stars',
        'water',
        'terrain',
      ].includes(this.object.objType);

    if (isParticleRoot) {
      return true;
    }

    if (this.object.visible === false) return false;
    return true;
  }

  refreshSelectionBounds() {
    this._refreshSelectionBoxGeometry();
  }

  attach(object) {
    if (!object) {
      this.object = undefined;
      this.visible = false;
      this._boxGroup.visible = false;
      if (this._cornerHandleGroup) this._cornerHandleGroup.visible = false;
      this.hoveredPartName = null;
      this.axis = null;
      this._updateGizmoHighlight();
      return this;
    }

    this.object = object;
    this.visible = this._isAttachedObjectVisible();

    // Check if it's a light or light helper
    const isLight =
      object.isLight ||
      object.type === 'AmbientLight' ||
      object.type === 'DirectionalLight' ||
      object.type === 'PointLight' ||
      object.type === 'SpotLight' ||
      object.type === 'RectAreaLight' ||
      object.type === 'AmbientLightHelper' ||
      object.type === 'DirectionalLightHelper' ||
      object.type === 'PointLightHelper' ||
      object.type === 'SpotLightHelper' ||
      object.type === 'AreaLightHelper';

    if (this.showSelectionBox && !isLight && this.visible) {
      object.updateMatrixWorld(true);
      this._lastBoxRecomputeTime = Date.now();
      this._unscaledBoxSize.copy(this._computeUnscaledBoundingBox(object));
      this._attachedObjectGeometryId = object.geometry?.id;
      this._lastObjectId = object.id;
      this._lastBoundsRootId = (
        resolveSelectionBoxFrame(object) || object
      ).uuid;
      object.matrixWorld.decompose(
        this._lastPosition,
        this._lastQuaternion,
        this._lastScale
      );

      this._createPointLineGeometry();
      this._syncSelectionBoxTransform();
      this._boxGroup.visible = true;
      if (this._cornerHandleGroup) this._cornerHandleGroup.visible = true;
      this._updateSpheresScale();
    } else {
      this._boxGroup.visible = false;
      if (this._cornerHandleGroup) this._cornerHandleGroup.visible = false;
    }

    this._updateGizmoHighlight();

    return this;
  }

  detach() {
    this.object = undefined;
    this.visible = false;
    this.dragging = false;
    this._bboxScaling = false;
    this._bboxHandleType = null;
    this._bboxHandleIndex = -1;
    this.hoveredPartName = null;
    this.axis = null;
    this._boxGroup.visible = false;
    if (this._cornerHandleGroup) this._cornerHandleGroup.visible = false;
    this._attachedObjectGeometryId = null;
    this._angleDisplay.style.display = 'none';
    this._updateGizmoHighlight();
    return this;
  }

  reset() {
    if (!this.enabled || !this.dragging) return;
    this.object.position.copy(this._positionStart);
    this.object.quaternion.copy(this._quaternionStart);
    this.object.scale.copy(this._scaleStart);
    this.dispatchEvent(_changeEvent);
    this.dispatchEvent(_objectChangeEvent);
    this.updateAngleDisplay();
  }

  getMode() {
    return this.mode;
  }
  setMode(mode) {
    this.mode = mode;
    this._updateGizmoHighlight();
    this.updateAngleDisplay();
  }
  setTranslationSnap(v) {
    this.translationSnap = v;
  }
  setRotationSnap(v) {
    this.rotationSnap = v;
  }
  setScaleSnap(v) {
    this.scaleSnap = v;
  }
  setSize(v) {
    this.size = v;
  }
  setSpace(v) {
    this.space = v;
    this.updateAngleDisplay();
  }

  _updateSpheresScale() {
    if (!this._points || !this.object || !this.camera) return;

    this.camera.updateMatrixWorld();
    this.camera.matrixWorld.decompose(
      this.cameraPosition,
      this.cameraQuaternion,
      this._cameraScale
    );

    // Box matrix is authored directly; keep its world matrix current for corners.
    this._boxGroup.updateMatrixWorld(true);

    const handleScale =
      computeTransformControlsScaleFactor(
        this.camera,
        this.worldPosition,
        this.cameraPosition,
        this.size,
        1
      ) * 1.35;

    // Uniform scale only — corners are NOT children of the scaled box group,
    // so non-uniform object scale cannot stretch them into ellipsoids.
    _uniformHandleScale.set(handleScale, handleScale, handleScale);

    // Convert corner world → TransformControls local without recursing into
    // updateMatrixWorld (this method runs during that update).
    _tempMatrix.copy(this.matrixWorld).invert();

    this._points.forEach((p) => {
      const localCorner = p.userData.localCorner;
      if (localCorner) {
        _cornerWorldPos
          .copy(localCorner)
          .applyMatrix4(this._boxGroup.matrixWorld);
      } else {
        _cornerWorldPos.set(0, 0, 0);
      }

      _cornerWorldPos.applyMatrix4(_tempMatrix);
      p.position.copy(_cornerWorldPos);
      p.scale.copy(_uniformHandleScale);
    });

    if (this._cornerHandleGroup) {
      this._cornerHandleGroup.visible = this._boxGroup.visible;
    }
  }
}

function getPointer(event) {
  if (this.domElement.ownerDocument.pointerLockElement) {
    return { x: 0, y: 0, button: event.button };
  }
  const rect = this.domElement.getBoundingClientRect();
  return {
    x: ((event.clientX - rect.left) / rect.width) * 2 - 1,
    y: (-(event.clientY - rect.top) / rect.height) * 2 + 1,
    button: event.button,
  };
}

function onPointerHover(event) {
  if (!this.enabled) return;
  if (['mouse', 'pen'].includes(event.pointerType)) {
    this.pointerHover(this._getPointer(event));
  }
}

function onPointerDown(event) {
  if (!this.enabled) return;
  if (!document.pointerLockElement)
    this.domElement.setPointerCapture(event.pointerId);
  this.domElement.addEventListener('pointermove', this._onPointerMove);
  this.pointerHover(this._getPointer(event));
  this.pointerDown(this._getPointer(event));
}

function onPointerMove(event) {
  if (this.enabled) this.pointerMove(this._getPointer(event));
}

function onPointerUp(event) {
  if (!this.enabled) return;
  this.domElement.releasePointerCapture(event.pointerId);
  this.domElement.removeEventListener('pointermove', this._onPointerMove);
  this.pointerUp(this._getPointer(event));
}

function intersectObjectWithRay(object, raycaster, includeInvisible = false) {
  const intersects = raycaster.intersectObject(object, true);
  for (const i of intersects) {
    if (i.object.visible || includeInvisible) return i;
  }
  return false;
}

// ────────────────────────────────────────────────
// Gizmo
// ────────────────────────────────────────────────

class TransformControlsGizmo extends Object3D {
  constructor() {
    super();
    this.isTransformControlsGizmo = true;
    this.type = 'TransformControlsGizmo';
    this.startAngle = 0;

    const gizmoMaterial = new THREE.MeshBasicMaterial({
      depthTest: false,
      depthWrite: false,
      fog: false,
      toneMapped: false,
      transparent: true,
    });
    const lineMaterial = new LineBasicMaterial({
      depthTest: false,
      depthWrite: false,
      fog: false,
      toneMapped: false,
      transparent: true,
    });

    // Spline-like palette: X blue, Y teal, Z coral red
    const matInvisible = gizmoMaterial.clone();
    matInvisible.opacity = 0;
    const matX = gizmoMaterial.clone();
    matX.color.setHex(0x2f8cff);
    const matY = gizmoMaterial.clone();
    matY.color.setHex(0x00e8c8);
    const matZ = gizmoMaterial.clone();
    matZ.color.setHex(0xff4a56);
    const matYellow = gizmoMaterial.clone();
    matYellow.color.setHex(0xffe14a);
    const matYellowTransparent = gizmoMaterial.clone();
    matYellowTransparent.color.setHex(0xffe14a);
    matYellowTransparent.opacity = 0.25;

    const scale = 2.5;
    const arrowPoints = [
      new Vector2(0.002 * scale, 0.049 * scale),
      new Vector2(0.004 * scale, 0.042 * scale),
      new Vector2(0.0061 * scale, 0.034 * scale),
      new Vector2(0.0083 * scale, 0.026 * scale),
      new Vector2(0.0095 * scale, 0.018 * scale),
      new Vector2(0.012 * scale, 0.0095 * scale),
      new Vector2(0.013 * scale, 0.0045 * scale),
      new Vector2(0.0065 * scale, 0.0015 * scale),
      new Vector2(0, 0),
    ];

    const arrowGeo = new LatheGeometry(arrowPoints, 15);
    const sphereGeo = new SphereGeometry(0.04, 16, 12);
    const shaftGeo = new CylinderGeometry(0.01, 0.01, 0.5, 4);
    shaftGeo.translate(0, 0.25, 0);

    const torusGeo = new TorusGeometry(0.3, 0.012, 8, 64, (Math.PI * 2) / 4);
    const meterGeo = new TorusGeometry(0.4, 0.005, 4, 64, Math.PI * 2);

    const needleGeo = new CylinderGeometry(0.002, 0.002, 0.38, 8);
    needleGeo.rotateZ(Math.PI / 2);
    needleGeo.translate(0.19, 0, 0);

    const needleMaterial = new LineBasicMaterial({
      color: 0xff4a56,
      transparent: true,
      opacity: 1.0,
    });
    needleMaterial.isAngleNeedle = true;

    const gizmoMap = {
      X: [
        [
          new Mesh(arrowGeo, matX.clone()),
          [0.5, 0, 0],
          [0, 0, -Math.PI / 2],
          null,
          'X-arrow',
        ],
        [
          new Mesh(sphereGeo, matX.clone()),
          [0.35, 0, 0],
          null,
          null,
          'X-sphere',
        ],
        [
          new Mesh(shaftGeo, matX.clone()),
          [0, 0, 0],
          [0, 0, -Math.PI / 2],
          null,
          'X-arrow',
        ],
      ],
      Y: [
        [
          new Mesh(arrowGeo, matY.clone()),
          [0, 0.5, 0],
          null,
          null,
          'Y-arrow',
        ],
        [
          new Mesh(sphereGeo, matY.clone()),
          [0, 0.35, 0],
          null,
          null,
          'Y-sphere',
        ],
        [new Mesh(shaftGeo, matY.clone()), null, null, null, 'Y-arrow'],
      ],
      Z: [
        [
          new Mesh(arrowGeo, matZ.clone()),
          [0, 0, 0.5],
          [Math.PI / 2, 0, 0],
          null,
          'Z-arrow',
        ],
        [
          new Mesh(sphereGeo, matZ.clone()),
          [0, 0, 0.35],
          null,
          null,
          'Z-sphere',
        ],
        [
          new Mesh(shaftGeo, matZ.clone()),
          null,
          [Math.PI / 2, 0, 0],
          null,
          'Z-arrow',
        ],
      ],
      // No visible orange center pivot — keep picker-only for free translate
      XY: [
        [new Mesh(torusGeo, matZ.clone()), [0.05, 0.05, 0], [0, 0, 0]],
      ],
      YZ: [
        [
          new Mesh(torusGeo, matX.clone()),
          [0, 0.05, 0.05],
          [0, -Math.PI / 2, 0],
        ],
      ],
      XZ: [
        [
          new Mesh(torusGeo, matY.clone()),
          [0.05, 0, 0.05],
          [Math.PI / 2, 0, 0],
        ],
      ],
      ANGLE: [
        [
          new Mesh(meterGeo, matYellow.clone()),
          [0, 0, 0],
          null,
          null,
          'angleMeter',
        ],
        [
          new Line(needleGeo, matZ.clone()),
          [0, 0, 0],
          null,
          null,
          'angleNeedle',
        ],
        [
          new Mesh(new SphereGeometry(0.025, 12, 12), matYellow.clone()),
          [0.4, 0, 0],
          null,
          null,
          'angleSphere',
        ],
        [
          new Mesh(new BufferGeometry(), matYellowTransparent.clone()),
          [0, 0, 0],
          null,
          null,
          'angleArc',
        ],
      ],
    };

    const pickerMap = {
      X: [
        [
          new Mesh(new CylinderGeometry(0.05, 0.05, 0.5, 8), matInvisible),
          [0.45, 0, 0],
          [0, 0, Math.PI / 2],
          null,
          'X-arrow',
        ],
        [
          new Mesh(new SphereGeometry(0.08, 12, 12), matInvisible),
          [0.35, 0, 0],
          null,
          null,
          'X-sphere',
        ],
      ],
      Y: [
        [
          new Mesh(new CylinderGeometry(0.05, 0.05, 0.5, 8), matInvisible),
          [0, 0.45, 0],
          null,
          null,
          'Y-arrow',
        ],
        [
          new Mesh(new SphereGeometry(0.08, 12, 12), matInvisible),
          [0, 0.35, 0],
          null,
          null,
          'Y-sphere',
        ],
      ],
      Z: [
        [
          new Mesh(new CylinderGeometry(0.05, 0.05, 0.5, 8), matInvisible),
          [0, 0, 0.45],
          [-Math.PI / 2, 0, 0],
          null,
          'Z-arrow',
        ],
        [
          new Mesh(new SphereGeometry(0.08, 12, 12), matInvisible),
          [0, 0, 0.35],
          null,
          null,
          'Z-sphere',
        ],
      ],
      XY: [
        [
          new Mesh(
            new TorusGeometry(0.3, 0.04, 4, 32, (Math.PI * 2) / 4),
            matInvisible
          ),
          [0.05, 0.05, 0],
          [0, 0, 0],
        ],
      ],
      YZ: [
        [
          new Mesh(
            new TorusGeometry(0.3, 0.04, 4, 32, (Math.PI * 2) / 4),
            matInvisible
          ),
          [0, 0.05, 0.05],
          [0, -Math.PI / 2, 0],
        ],
      ],
      XZ: [
        [
          new Mesh(
            new TorusGeometry(0.3, 0.04, 4, 32, (Math.PI * 2) / 4),
            matInvisible
          ),
          [0.05, 0, 0.05],
          [Math.PI / 2, 0, 0],
        ],
      ],
    };

    function setup(map) {
      const g = new Object3D();
      for (const name in map) {
        map[name].forEach(([mesh, pos, rot, scl, tag]) => {
          const m = mesh.clone();
          m.name = tag || name;
          m.tag = tag;
          if (pos) m.position.set(...pos);
          if (rot) m.rotation.set(...rot);
          if (scl) m.scale.set(...scl);
          m.updateMatrix();
          if (m.geometry) {
            const geom = m.geometry.clone();
            geom.applyMatrix4(m.matrix);
            m.geometry = geom;
          }
          m.position.set(0, 0, 0);
          m.rotation.set(0, 0, 0);
          m.scale.set(1, 1, 1);
          m.renderOrder = 1000;
          m.frustumCulled = false;
          g.add(m);
        });
      }
      return g;
    }

    this.gizmo = setup(gizmoMap);
    this.picker = setup(pickerMap);

    this.add(this.gizmo);
    this.add(this.picker);

    this.picker.visible = false;
    disableMeshShadows(this);
  }

  updateMatrixWorld(force) {
    this.position.copy(this.worldPosition);
    this.gizmo.visible = true;

    const scaleFactor = computeTransformControlsScaleFactor(
      this.camera,
      this.worldPosition,
      this.cameraPosition,
      this.parent?.size,
      1.4
    );

    this.gizmo.scale.setScalar(scaleFactor);
    this.picker.scale.setScalar(scaleFactor);

    const gizmoQuaternion = this.parent?.worldQuaternion || new Quaternion();

    this.gizmo.children.forEach((child) => {
      if (
        child.tag &&
        ['angleMeter', 'angleNeedle', 'angleArc', 'angleSphere'].includes(
          child.tag
        )
      )
        return;
      child.quaternion.copy(gizmoQuaternion);
    });

    this.picker.children.forEach((child) => {
      child.quaternion.copy(gizmoQuaternion);
    });

    const isActiveRotation =
      this.parent?.mode === 'rotate' &&
      this.parent?.dragging === true &&
      this.parent?.axis &&
      !['E', 'XYZE'].includes(this.parent?.axis);

    this.gizmo.children.forEach((child) => {
      if (
        child.tag &&
        ['angleMeter', 'angleNeedle', 'angleArc', 'angleSphere'].includes(
          child.tag
        )
      )
        return;
      child.visible = !isActiveRotation;
    });

    this.picker.visible = !isActiveRotation;

    const angleParts = this.gizmo.children.filter(
      (c) =>
        c.tag &&
        ['angleMeter', 'angleNeedle', 'angleArc', 'angleSphere'].includes(c.tag)
    );

    const shouldShowAngle = isActiveRotation;

    angleParts.forEach((part) => {
      part.visible = shouldShowAngle;

      if (!shouldShowAngle) {
        if (part.tag === 'angleArc') {
          part.geometry?.dispose();
          part.geometry = new BufferGeometry();
        }
        return;
      }

      let baseQuat = gizmoQuaternion.clone();

      let axisQ = new Quaternion();
      if (this.parent.axis === 'X')
        axisQ.setFromEuler(new Euler(0, Math.PI / 2, 0));
      else if (this.parent.axis === 'Y')
        axisQ.setFromEuler(new Euler(Math.PI / 2, 0, 0));
      else if (this.parent.axis === 'Z') axisQ.setFromEuler(new Euler(0, 0, 0));
      else if (this.parent.axis === 'XY')
        axisQ.setFromEuler(new Euler(0, 0, 0));
      else if (this.parent.axis === 'YZ')
        axisQ.setFromEuler(new Euler(0, Math.PI / 2, 0));
      else if (this.parent.axis === 'XZ')
        axisQ.setFromEuler(new Euler(-Math.PI / 2, 0, 0));

      part.quaternion.copy(baseQuat).multiply(axisQ);

      if (['angleNeedle', 'angleSphere', 'angleMeter'].includes(part.tag)) {
        const rot = new Quaternion().setFromAxisAngle(
          new Vector3(0, 0, 1),
          this.parent.rotationAngle || 0
        );
        part.quaternion.multiply(rot);
      }

      if (part.tag === 'angleArc') {
        const geo = generateArcGeometry(0, this.parent.rotationAngle || 0);
        part.geometry?.dispose();
        part.geometry = geo;
        part.material.color.setHex(0xffff00);
        part.renderOrder = 2000;
      }
    });

    super.updateMatrixWorld(force);
  }
}

function generateArcGeometry(start, end, radius = 0.4, segments = 64) {
  const positions = [0, 0, 0];
  let s = start % (Math.PI * 2);
  if (s < 0) s += Math.PI * 2;
  let e = end;
  let delta = e - s;
  if (Math.abs(delta) < 0.001) delta = delta >= 0 ? 0.001 : -0.001;

  const steps = Math.max(
    segments,
    Math.ceil((Math.abs(delta) * segments) / (Math.PI * 2))
  );

  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const ang = s + t * delta;
    positions.push(radius * Math.cos(ang), radius * Math.sin(ang), 0);
  }

  const indices = [];
  for (let i = 1; i < positions.length / 3; i++) {
    indices.push(0, i, i + 1);
  }

  const geo = new BufferGeometry();
  geo.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

class TransformControlsPlane extends Mesh {
  constructor() {
    super(
      new PlaneGeometry(100000, 100000, 2, 2),
      new MeshBasicMaterial({
        visible: false,
        wireframe: true,
        side: DoubleSide,
        transparent: true,
        opacity: 0.1,
      })
    );
    this.isTransformControlsPlane = true;
    this.type = 'TransformControlsPlane';
    this.castShadow = false;
    this.receiveShadow = false;
  }

  updateMatrixWorld(force) {
    const v1 = new Vector3();
    const v2 = new Vector3();
    const v3 = new Vector3();
    const alignVector = new Vector3();
    const tempMatrix = new Matrix4();
    const dir = new Vector3();

    this.position.copy(this.worldPosition);

    const effectiveSpace =
      this.mode === 'translate' || this.mode === 'scale' ? 'local' : this.space;

    v1.copy(_unit.X).applyQuaternion(
      effectiveSpace === 'local' ? this.worldQuaternion : new Quaternion()
    );
    v2.copy(_unit.Y).applyQuaternion(
      effectiveSpace === 'local' ? this.worldQuaternion : new Quaternion()
    );
    v3.copy(_unit.Z).applyQuaternion(
      effectiveSpace === 'local' ? this.worldQuaternion : new Quaternion()
    );

    alignVector.copy(v2);

    if (this.mode === 'translate' || this.mode === 'scale') {
      switch (this.axis) {
        case 'X':
          alignVector.copy(this.eye).cross(v1);
          dir.copy(v1).cross(alignVector);
          break;
        case 'Y':
          alignVector.copy(this.eye).cross(v2);
          dir.copy(v2).cross(alignVector);
          break;
        case 'Z':
          alignVector.copy(this.eye).cross(v3);
          dir.copy(v3).cross(alignVector);
          break;
        case 'XY':
          dir.copy(v3);
          break;
        case 'YZ':
          dir.copy(v1);
          break;
        case 'XZ':
          dir.copy(v2);
          break;
        default:
          dir.set(0, 0, 0);
      }
    }

    if (dir.lengthSq() === 0) {
      this.quaternion.copy(this.cameraQuaternion);
    } else {
      tempMatrix.lookAt(new Vector3(), dir, alignVector);
      this.quaternion.setFromRotationMatrix(tempMatrix);
    }

    super.updateMatrixWorld(force);
  }
}

export { TransformControls, TransformControlsGizmo, TransformControlsPlane };
